import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Trash2, Tag, Clock } from 'lucide-react';
import { calculateLoyaltyDiscounts } from '../../../../lib/loyalty';
import { supabase } from '../../../../lib/supabase';
import { PromotionDialog } from './PromotionDialog';

interface ServicePromotion {
  type: 'percentage' | 'fixed';
  value: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
}

interface Service {
  id: string;
  name: string;
  name_he: string;
  price: number;
  duration: number | string;
  business_id: string;
  promotion?: ServicePromotion;
  image_url?: string;
  description?: string;
}

interface ServiceCardProps {
  service: Service;
  onEdit: (service: Service) => void;
  onDelete: (serviceId: string) => void;
  onPromotionUpdate?: (serviceId: string, promotion: ServicePromotion | null) => void;
  customerId?: string;
  businessSettings?: any;
}

interface LoyaltyBenefits {
  services_discount: number;
  products_discount: number;
  birthday_appointment: boolean;
  free_appointment_every: number | null;
}

export function ServiceCard({ service, onEdit, onDelete, onPromotionUpdate, customerId, businessSettings }: ServiceCardProps) {
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);

  const isPromotionValid = (promotion: Service['promotion']) => {
    if (!promotion?.is_active) return false;

    const now = new Date().getTime();
    const startDate = promotion.start_date ? new Date(promotion.start_date).getTime() : null;
    const endDate = promotion.end_date ? new Date(promotion.end_date).getTime() : null;

    if (endDate && now > endDate) return false;
    if (startDate && now < startDate) return false;

    return true;
  };

  const hasPromotion = service.promotion && isPromotionValid(service.promotion);
  const originalPrice = service.price.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  let finalPrice = service.price;

  if (hasPromotion && service.promotion) {
    if (service.promotion.discount_type === 'percentage') {
      finalPrice = finalPrice * (1 - service.promotion.discount_value / 100);
    } else {
      finalPrice = finalPrice - (service.promotion?.discount_value || 0);
    }
  }

  const formattedPrice = finalPrice.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const parseDuration = (duration: number | string): number => {
    if (typeof duration === 'number') return duration;
    const match = duration.match(/(\d+):(\d+):(\d+)/);
    if (match) {
      return parseInt(match[1]) * 60 + parseInt(match[2]);
    }
    return 30;
  };

  const calculateFinalPrice = async (customerId?: string) => {
    let finalPrice = service.price;
    
    // Apply promotion if exists and is active
    if (service.promotion?.is_active) {
      if (service.promotion.type === 'percentage') {
        finalPrice = finalPrice * (1 - service.promotion.value / 100);
      } else {
        finalPrice = finalPrice - service.promotion.value;
      }
    }

    // Apply loyalty discount if customer is provided
    if (customerId && businessSettings?.loyalty?.enabled) {
      const { data: customer } = await supabase
        .from('customers')
        .select('loyalty_level')
        .eq('id', customerId)
        .single();

      if (customer?.loyalty_level && businessSettings.loyalty.levels[customer.loyalty_level]) {
        const benefits = businessSettings.loyalty.levels[customer.loyalty_level].benefits;
        if (benefits.services_discount > 0) {
          finalPrice = finalPrice * (1 - benefits.services_discount / 100);
    }
  }
    }

    return finalPrice.toFixed(2);
  };

  const durationMinutes = parseDuration(service.duration);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-all"
    >
      {hasPromotion && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-3 py-1.5 flex items-center justify-center gap-1.5">
          <Tag className="h-3.5 w-3.5 text-white" />
          <span className="text-xs font-semibold text-white">
            {service.promotion.discount_type === 'percentage'
              ? `${service.promotion.discount_value}% הנחה`
              : `הנחה של ${service.promotion.discount_value}₪`}
          </span>
        </div>
      )}

      <div className="p-4">
        <div className="flex gap-3">
          {service.image_url ? (
            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
              <img
                src={service.image_url}
                alt={service.name_he}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Clock className="h-8 w-8 text-white" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base mb-2">{service.name_he}</h3>

            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-lg">
                <Clock className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">{durationMinutes} דק׳</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                {hasPromotion && (
                  <span className="text-sm text-gray-400 line-through">₪{originalPrice}</span>
                )}
                <span className="text-xl font-bold text-gray-900">₪{formattedPrice}</span>
              </div>

              <div className="flex gap-1">
                <button
                  onClick={() => setShowPromotionDialog(true)}
                  className={`p-2 transition-colors rounded-lg ${
                    hasPromotion
                      ? 'text-green-600 bg-green-50 active:bg-green-100'
                      : 'text-gray-400 active:text-green-600 active:bg-green-50'
                  }`}
                  title="מבצע"
                >
                  <Tag className="h-4.5 w-4.5" />
                </button>
                <button
                  onClick={() => onEdit(service)}
                  className="p-2 text-gray-400 active:text-blue-600 transition-colors rounded-lg active:bg-blue-50"
                  title="עריכה"
                >
                  <Edit2 className="h-4.5 w-4.5" />
                </button>
                <button
                  onClick={() => onDelete(service.id)}
                  className="p-2 text-gray-400 active:text-red-600 transition-colors rounded-lg active:bg-red-50"
                  title="מחיקה"
                >
                  <Trash2 className="h-4.5 w-4.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {service.description && (
          <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-2">{service.description}</p>
        )}
      </div>

      <PromotionDialog
        isOpen={showPromotionDialog}
        onClose={() => setShowPromotionDialog(false)}
        onSave={(promotion) => {
          if (onPromotionUpdate) {
            onPromotionUpdate(service.id, promotion);
          }
        }}
        currentPromotion={service.promotion}
        serviceName={service.name_he}
      />
    </motion.div>
  );
}